import { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import { getToken, onMessage } from "firebase/messaging";
import { getFirebaseMessaging } from "@/src/lib/util/firebase";
import { commonApiService } from "@/src/lib/api-services/commonApiService";
import NotificationPopup from "@/src/components/common/push-notification/pushNotificationPopup";
import { FIREBASE_WEB_PUSH_CERT } from "@/src/lib/util/config";

export default function PushNotifications() {
    const [token, setToken] = useState(null);
    const userInfo = useSelector((state) => state?.user?.userInfo);
    const messageListenerRegistered = useRef(false);

    const [showPopup, setShowPopup] = useState(false);
    const [popupProps, setPopupProps] = useState({
        title: "",
        message: "",
        imageUrl: "",
        link: "",
    });

    // Register message listener once (doesn't depend on userInfo)
    useEffect(() => {
        const registerMessageListener = async () => {
            try {
                const messaging = await getFirebaseMessaging();
                if (!messaging || messageListenerRegistered.current) return;

                onMessage(messaging, (payload) => {
                    console.log("Foreground message payload:", payload);

                    const title = payload?.notification?.title || "";
                    const body = payload?.notification?.body || "";
                    const image = payload?.notification?.image || "";
                    const link = payload?.data?.url || "";

                    setPopupProps({
                        title,
                        message: body,
                        imageUrl: image,
                        link,
                    });
                    setShowPopup(true);
                });
                messageListenerRegistered.current = true;
            } catch (err) {
                console.error("Failed to register message listener:", err);
            }
        };

        registerMessageListener();
    }, []);

    // Request permission and save token (depends on userInfo)
    useEffect(() => {
        // Only proceed if userInfo has mobileNo
        if (!userInfo?.mobileNo) {
            console.log("Waiting for userInfo.mobileNo...", userInfo);
            return;
        }

        const requestPermission = async () => {
            // Check current permission status first
            if (typeof window === "undefined" || !("Notification" in window)) {
                console.log("Notifications not supported in this browser.");
                return;
            }

            // If permission is already granted, proceed directly
            if (Notification.permission === "granted") {
                // Permission already granted, proceed to get token
            } else if (Notification.permission === "default") {
            // Permission not yet requested, ask for it
                const permission = await Notification.requestPermission();
                if (permission !== "granted") {
                    console.log("Notification permission not granted.");
                    return;
                }
            } else {
                // Permission was denied, browser won't show prompt again
                console.log("Notification permission was previously denied. User needs to enable it in browser settings.");
                return;
            }

            // Proceed with getting token if permission is granted
            if (Notification.permission === "granted") {
                try {
                    const messaging = await getFirebaseMessaging();
                    if (!messaging) return; // FCM not supported in this browser

                    const fcmToken = await getToken(messaging, {
                        vapidKey: FIREBASE_WEB_PUSH_CERT,
                    });
                    setToken(fcmToken);

                    if (fcmToken && userInfo?.mobileNo) {
                        const savedToken =
                            localStorage.getItem("firebaseToken");
                        console.log(
                            "Saved token:",
                            userInfo?.mobileNo,
                            fcmToken
                        );
                        try {
                            const response =
                                await commonApiService.saveDeviceToken({
                                    deviceToken: fcmToken,
                                    mobileNo: userInfo?.mobileNo,
                                    platform: "web",
                                    isActive: 1,
                                });
                            if (response?.success) {
                                localStorage.setItem("firebaseToken", fcmToken);
                            } else {
                                console.error(
                                    "Failed to send token to backend."
                                );
                            }
                        } catch (error) {
                            console.error(
                                "Error sending device token to backend:",
                                error
                            );
                        }
                    }
                } catch (err) {
                    console.error("Failed to get FCM token:", err);
                }
            }
        };

        requestPermission();
    }, [userInfo]);

    return (
        <>
            {showPopup && (
                <NotificationPopup
                    props={popupProps}
                    onClose={() => setShowPopup(false)}
                />
            )}
        </>
    );
}
